import { exportGroupFileName } from './ExportCompetitorGroups';
import React, { Fragment, useRef, useState } from 'react';
import moment from 'moment';
import {
	Dialog,
	DialogPanel,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

import Utilities from '@util/Utilities';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import Spinner from '@app/component/static/Spinner';
import MarketStateExportReportInput from '@app/model/market_state/_export/input/MarketStateExportReportInput';
import MarketStateExportService from '@app/model/market_state/_export/service/MarketStateExportService';
import { MarketStateExportReportPeriod } from '@app/model/market_state/_export/MarketStateExportReport';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import MarketStateExportFilter from '@app/model/market_state/_export/MarketStateExportFilter';
import { useSiteNotificationTools } from '@app/site/secured/rateshopper/rates/provider/useSiteNotificationTools';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import ExportToExcelSettingsForm, {
	ExportToExcelSettingsFormHandle,
}
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/export/ExportToExcelSettingsForm';
import Modal from '@app/component/overlays/modal/Modal';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import ExportToExcelScheduleReportModal
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/export/ExportToExcelScheduleReportModal';

type ExportOption = {
	period: MarketStateExportReportPeriod;
	monthCount: number;
};

const EXPORT_OPTIONS: ExportOption[] = [
	{ period: MarketStateExportReportPeriod.CURRENT_MONTH, monthCount: 1 },
	{ period: MarketStateExportReportPeriod.MONTHS_3, monthCount: 3 },
	{ period: MarketStateExportReportPeriod.MONTHS_6, monthCount: 6 },
	{ period: MarketStateExportReportPeriod.MONTHS_12, monthCount: 12 },
];

const createSafeFileNamePart = (value: string): string => value
	.trim()
	.replace(/\s+/g, '_')
	.replace(/[^a-zA-Z0-9_-]/g, '')
	.toLowerCase();

const buildFilterFileNamePart = (filter: MarketStateExportFilter, companyHotel: CompanyHotel): string => {
	const parts: string[] = [filter.provider !== undefined ? String(filter.provider) : 'any'];

	if (filter.platform !== undefined) parts.push(String(filter.platform));
	if (filter.region !== undefined) parts.push(String(filter.region));
	if (filter.ratesType !== undefined) parts.push(String(filter.ratesType));
	if (typeof filter.guests === 'number') parts.push(`guests${filter.guests}`);
	if (typeof filter.nights === 'number') parts.push(`los${filter.nights}`);
	if (filter.meal !== undefined) parts.push(String(filter.meal));
	if (filter.refunding !== undefined) parts.push(String(filter.refunding));
	if (filter.airportCode !== undefined) parts.push(String(filter.airportCode));
	if ((filter.roomTypes?.length ?? 0) > 0) parts.push(filter.roomTypes.map((rt: HotelRoomMappingType) => String(rt)).join('_'));

	const groupPart = exportGroupFileName(filter.competitorTagIds, companyHotel.competitorTags);
	if (groupPart) parts.push(groupPart);
	return createSafeFileNamePart(parts.join('_'));
};

const buildExportName = (
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	optionKey: string,
	filter: MarketStateExportFilter,
): string => {
	const hotelName = companyHotel.mainHotel.name ?? 'hotel';
	const shortHotelNamePart = createSafeFileNamePart(hotelName).substring(0, 10);
	const shortHotelName = shortHotelNamePart.length > 0 ? shortHotelNamePart : 'hotel';
	const createdAt = Utilities.formatDate(new Date(), 'DD-MMM-yyyy');
	const filterName = buildFilterFileNamePart(filter, companyHotel);

	return `${shortHotelName}_${calendarMonth.index.month}_${createdAt}_${optionKey}_${filterName}`.toLowerCase();
};

const toExportNamePeriodPart = (period: MarketStateExportReportPeriod): string => period.toLowerCase();

const getSelectedMonthStart = (calendarMonth: CalendarMonth): moment.Moment => moment(new Date(
	calendarMonth.index.year,
	Utilities.getMonthOrdinal(calendarMonth.index.month),
	1,
));

const getViewedMonthDateRange = (
	calendarMonth: CalendarMonth,
	monthCount: number,
): { fromDate: string; toDate: string } => {
	const start = getSelectedMonthStart(calendarMonth);
	const end = start.clone().add(monthCount - 1, 'months').endOf('month');

	return {
		fromDate: start.format('YYYY-MM-DD'),
		toDate: end.format('YYYY-MM-DD'),
	};
};

const formatViewedMonthRangeLabel = (calendarMonth: CalendarMonth, monthCount: number): string => {
	const start = getSelectedMonthStart(calendarMonth);
	const end = start.clone().add(monthCount - 1, 'months');

	if (monthCount === 1) return start.format('MMMM YYYY');

	if (start.year() === end.year()) {
		return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
	}

	return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
};

const ExportToExcelSelectorFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel;
	calendarMonth: CalendarMonth;
}> = ({ companyHotel, calendarMonth }) => {
	const { filter } = useMarketStateFilterInputTools();
	const toaster = useToaster();
	const siteNotificationTools = useSiteNotificationTools();
	const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
	const settingsFormRef = useRef<ExportToExcelSettingsFormHandle | null>(null);
	const settingsModalRef = useRef<ModalHandler | null>(null);

	const [isExporting, setIsExporting] = useState(false);
	const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
	const [showExcelSettings, setShowExcelSettings] = useState(false);
	const [showScheduleReport, setShowScheduleReport] = useState(false);
	const [isSavingExcelSettings, setIsSavingExcelSettings] = useState(false);
	const [customStartDate, setCustomStartDate] = useState('');
	const [customEndDate, setCustomEndDate] = useState('');

	const exportFilter = { ...filter };
	const isDateSelectionValid = customStartDate.length > 0 && customEndDate.length > 0 && customStartDate <= customEndDate;
	const exportOptions = EXPORT_OPTIONS.map(option => ({
		...option,
		label: formatViewedMonthRangeLabel(calendarMonth, option.monthCount),
	}));

	const saveExport = async (
		period: MarketStateExportReportPeriod,
		customDates?: { fromDate: string; toDate: string },
		exportNamePeriodPart = toExportNamePeriodPart(period),
	): Promise<void> => {
		setIsExporting(true);

		try {
			const input: MarketStateExportReportInput = {
				companyHotelId: companyHotel.id,
				period,
				...customDates,
				name: buildExportName(companyHotel, calendarMonth, exportNamePeriodPart, exportFilter),
				filter: exportFilter,
			};

			await MarketStateExportService.saveMarketStateExportReport(input);
			toaster.success('Downloads', 'Report processing has been started.');
			siteNotificationTools.refetch();
		} catch (error) {
			console.error('Error saving export:', error);
			toaster.error('Downloads', 'Error saving export. Please try again later!');
		} finally {
			setIsExporting(false);
		}
	};

	const handleExportOptionSelect = (option: ExportOption): void => {
		void saveExport(
			MarketStateExportReportPeriod.CUSTOM_DATES,
			getViewedMonthDateRange(calendarMonth, option.monthCount),
			toExportNamePeriodPart(option.period),
		);
	};

	const handleCustomExport = (): void => {
		if (!isDateSelectionValid) return;

		setShowCustomDatePicker(false);
		void saveExport(
			MarketStateExportReportPeriod.CUSTOM_DATES,
			{ fromDate: customStartDate, toDate: customEndDate },
		);
	};

	return (
		<>
			<Menu as="div" className="relative">
				<MenuButton
					type="button"
					className="btn btn-white font-normal py-1.5 flex items-center cursor-pointer"
					disabled={isExporting}
				>
					{isExporting ? <Spinner /> : <DocumentArrowDownIcon className="-ml-1 mr-1 h-4 w-4" />}
					{isExporting ? ' Exporting...' : 'Export'}
					<ChevronDownIcon className="-mr-1 ml-1 h-5 w-5 text-blue-500" />
				</MenuButton>

				{!isExporting && (
					<Transition
						as={Fragment}
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
					>
						<MenuItems className="absolute right-0 z-10 mt-3 w-44 origin-top-right overflow-hidden rounded-md bg-white divide-y divide-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
							<div className="px-4 py-3">
								<p className="text-sm font-semibold text-gray-900">Export ({companyHotel.settings.calendarExport.format === 'CSV' ? 'CSV (ZIP)' : 'XLSX'})</p>
							</div>
							<div className="py-1">
								{exportOptions.map((option) => (
									<MenuItem key={option.period}>
										{({ focus }) => (
											<button
												type="button"
												onClick={() => handleExportOptionSelect(option)}
												className={Utilities.classNames(
													focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
													'block w-full px-4 py-2 text-xs text-left cursor-pointer',
												)}
											>
												{option.label}
											</button>
										)}
									</MenuItem>
								))}
								<MenuItem>
									{({ focus }) => (
										<button
											type="button"
											onClick={() => setShowCustomDatePicker(true)}
											className={Utilities.classNames(
												focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
												'block w-full px-4 py-2 text-xs text-left cursor-pointer',
											)}
										>
											Custom dates
										</button>
									)}
								</MenuItem>
							</div>
							<div className="py-1">
								<MenuItem>
									{({ focus }) => (
										<button
											type="button"
											onClick={() => setShowExcelSettings(true)}
											className={Utilities.classNames(
												focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
												'block w-full px-4 py-2 text-xs text-left cursor-pointer',
											)}
										>
											Export settings
										</button>
									)}
								</MenuItem>
							</div>
							<div className="py-1">
								<MenuItem>
									{({ focus }) => (
										<button
											type="button"
											onClick={() => setShowScheduleReport(true)}
											className={Utilities.classNames(
												focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
												'block w-full px-4 py-2 text-xs text-left cursor-pointer',
											)}
										>
											Schedule Report
										</button>
									)}
								</MenuItem>
							</div>
							<div className="px-4 py-3">
								<Link
									to="/secured/rateshopper/rates/market-state-exports"
									className="btn btn-primary font-normal text-xs py-1.5 w-full"
								>
									All Downloads
								</Link>
							</div>
						</MenuItems>
					</Transition>
				)}
			</Menu>

			<Transition show={showCustomDatePicker} as={Fragment}>
				<Dialog
					as="div"
					className="relative z-10"
					initialFocus={cancelButtonRef}
					onClose={setShowCustomDatePicker}
				>
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
					</TransitionChild>

					<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
						<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
							<TransitionChild
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
								enterTo="opacity-100 translate-y-0 sm:scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 translate-y-0 sm:scale-100"
								leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							>
								<DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
									<div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
										<button
											type="button"
											className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 cursor-pointer"
											onClick={() => setShowCustomDatePicker(false)}
										>
											<span className="sr-only">Close</span>
											<XMarkIcon className="h-6 w-6" aria-hidden="true" />
										</button>
									</div>

									<div className="inner-wrapper px-4 pt-4 pb-3 mt-3 sm:px-6 text-left">
										<Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
											Export with Custom Dates
										</Dialog.Title>

										<div className="mt-6">
											<p className="mb-2 text-base text-gray-600">Select your start and end dates:</p>
											<div className="mt-0.5 sm:grid grid-cols-2 gap-4">
												<div className="col-span-1">
													<label className="label">Start Date</label>
													<input
														className="input cursor-pointer"
														type="date"
														value={customStartDate}
														onChange={(e) => setCustomStartDate(e.target.value)}
													/>
												</div>
												<div className="col-span-1">
													<label className="label">End Date</label>
													<input
														className="input cursor-pointer"
														type="date"
														value={customEndDate}
														min={customStartDate.length > 0 ? customStartDate : undefined}
														onChange={(e) => setCustomEndDate(e.target.value)}
													/>
												</div>
											</div>
										</div>
									</div>

									<div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
										<button
											type="button"
											className="btn btn-primary sm:ml-3"
											disabled={!isDateSelectionValid || isExporting}
											onClick={handleCustomExport}
										>
											Export
										</button>
										<button
											type="button"
											className="btn btn-white mt-3 sm:mt-0"
											onClick={() => setShowCustomDatePicker(false)}
											ref={cancelButtonRef}
										>
											Cancel
										</button>
									</div>
								</DialogPanel>
							</TransitionChild>
						</div>
					</div>
				</Dialog>
			</Transition>

			<Modal
				ref={settingsModalRef}
				show={showExcelSettings}
				onClose={() => setShowExcelSettings(false)}
				title="Export settings"
				subtitle="Choose the format and additional columns for future exports."
				actions={<>
					<button
						type="button"
						className="btn btn-white"
						disabled={isSavingExcelSettings}
						onClick={() => settingsFormRef.current?.reset()}
					>
						Reset
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={isSavingExcelSettings}
						onClick={() => settingsFormRef.current?.submit()}
					>
						Save
					</button>
				</>}
			>
				<ExportToExcelSettingsForm
					ref={settingsFormRef}
					companyHotel={companyHotel}
					onPendingChange={setIsSavingExcelSettings}
					onSaved={() => settingsModalRef.current?.close()}
				/>
			</Modal>

			<ExportToExcelScheduleReportModal
				show={showScheduleReport}
				onClose={() => setShowScheduleReport(false)}
				companyHotel={companyHotel}
				currentFilter={exportFilter}
			/>
		</>
	);
};

export default ExportToExcelSelectorFragment;
